// Artifact evidence belongs to the available file. export_delivery describes
// the latest attempt separately, including retained same-version downloads.
const STATES = new Set(["passed", "failed", "not_checked"]);
/** @type {Record<string, string[]>} */
const OUTPUTS = { branded_pdf: ["report_pdf"], deck: ["deck", "pptx"], one_pager: ["one_pager"] };
/** @type {Record<string, string>} */
const LABELS = { branded_pdf: "Branded PDF", deck: "Seller Presentation", one_pager: "One-pager" };

const QUALITY_KEYS = [
  "version",
  "status",
  "integrity_status",
  "visual_status",
  "reason_codes",
  "page_count",
  "text_char_count",
  "image_count",
];
const INTEGRITY_FAILURES = [
  "pdf_invalid",
  "pdf_encrypted",
  "pdf_empty",
  "pdf_page_count",
  "pdf_page_geometry",
  "pdf_content_missing",
];
const INSPECTION_FAILURES = [
  "pdf_inspection_unavailable",
  "pdf_inspection_timeout",
  "pdf_inspection_limit",
];
const REASONS = new Set([
  ...INTEGRITY_FAILURES,
  ...INSPECTION_FAILURES,
  "visual_failed",
  "visual_not_checked",
]);

/** @param {any} value @param {string} [artifactKey] */
export function artifactQuality(value, artifactKey = "report_pdf") {
  if (
    !value ||
    Object.keys(value).length !== QUALITY_KEYS.length ||
    !QUALITY_KEYS.every((key) => Object.prototype.hasOwnProperty.call(value, key)) ||
    value.version !== 1 ||
    !STATES.has(value.status) ||
    !STATES.has(value.integrity_status) ||
    !STATES.has(value.visual_status) ||
    !Array.isArray(value.reason_codes) ||
    value.reason_codes.some(
      (/** @type {unknown} */ code) => typeof code !== "string" || !REASONS.has(code)
    ) ||
    new Set(value.reason_codes).size !== value.reason_codes.length ||
    ["page_count", "text_char_count", "image_count"].some(
      (key) =>
        value[key] !== null &&
        (!Number.isSafeInteger(value[key]) || value[key] < 0 || value[key] > 2147483647)
    )
  )
    return null;
  const reasons = new Set(value.reason_codes);
  const failures = INTEGRITY_FAILURES.filter((code) => reasons.has(code)).length;
  const inspections = INSPECTION_FAILURES.filter((code) => reasons.has(code)).length;
  if (
    failures !== (value.integrity_status === "failed" ? 1 : 0) ||
    inspections !== (value.integrity_status === "not_checked" ? 1 : 0) ||
    reasons.has("visual_failed") !== (value.visual_status === "failed") ||
    reasons.has("visual_not_checked") !== (value.visual_status === "not_checked")
  )
    return null;
  if (value.integrity_status === "passed") {
    const pageCap = artifactKey === "one_pager" ? 1 : artifactKey === "deck" ? 60 : 200;
    if (
      !Number.isInteger(value.page_count) ||
      value.page_count < 1 ||
      value.page_count > pageCap ||
      value.text_char_count === null ||
      value.text_char_count > 2000000 ||
      value.image_count === null ||
      value.image_count > 10000
    )
      return null;
  }
  const expected =
    value.integrity_status === "failed" || value.visual_status === "failed"
      ? "failed"
      : value.integrity_status === "passed" && value.visual_status === "passed"
        ? "passed"
        : "not_checked";
  return value.status === expected ? value : null;
}

/** @param {any} artifact @param {string} [artifactKey] */
export function artifactUsable(artifact, artifactKey = "report_pdf") {
  if (artifact?.available !== true) return false;
  if (!Object.prototype.hasOwnProperty.call(artifact, "quality")) return true;
  const quality = artifactQuality(artifact.quality, artifactKey);
  return !!quality && quality.integrity_status === "passed" && quality.visual_status !== "failed";
}

/** @param {any} artifact @param {string} [artifactKey] */
export function artifactQualityMessage(artifact, artifactKey = "report_pdf") {
  if (!Object.prototype.hasOwnProperty.call(artifact || {}, "quality")) {
    return "Automated file checks are unknown. Review before sharing.";
  }
  const quality = artifactQuality(artifact.quality, artifactKey);
  if (!quality || quality.integrity_status !== "passed") {
    return quality?.status === "failed"
      ? "File withheld: a quality check failed."
      : "File withheld: its integrity has not been verified.";
  }
  if (quality.visual_status === "failed") return "File withheld: a visual quality check failed.";
  if (artifact?.available !== true) return "File unavailable: it could not be published.";
  if (quality.visual_status === "not_checked") {
    return "File integrity checked. Appearance not checked — review before sharing.";
  }
  return "Automated file checks passed. Review before sharing.";
}

/** @param {any} doc @param {string | null} [jobId] */
export function exportDelivery(doc, jobId = null) {
  const value = doc?.export_delivery;
  if (
    !value ||
    value.version !== 1 ||
    typeof value.job_id !== "string" ||
    !value.job_id ||
    (jobId && value.job_id !== jobId) ||
    !Number.isSafeInteger(value.document_version) ||
    value.document_version < 1 ||
    value.document_version !== doc.version ||
    !Array.isArray(value.units) ||
    value.units.length > 3
  )
    return null;
  const seen = new Set();
  let refunds = 0;
  for (const unit of value.units) {
    const outputs = OUTPUTS[unit?.artifact];
    if (
      !Array.isArray(outputs) ||
      seen.has(unit.artifact) ||
      !["delivered", "held"].includes(unit.status) ||
      !["", "quality_failed", "quality_not_checked", "artifact_failed"].includes(
        unit.reason_code
      ) ||
      (unit.status === "delivered" && unit.reason_code !== "") ||
      (unit.status === "held" && unit.reason_code === "") ||
      (unit.status === "delivered" && unit.preserved_outputs?.length !== 0) ||
      !Array.isArray(unit.outputs) ||
      unit.outputs.length !== outputs.length ||
      !outputs.every((key) => unit.outputs.includes(key)) ||
      !Array.isArray(unit.preserved_outputs) ||
      unit.preserved_outputs.some((/** @type {string} */ key) => !outputs.includes(key))
    )
      return null;
    const settlement = unit.settlement;
    if (
      !settlement ||
      !Number.isSafeInteger(settlement.charged_credits) ||
      settlement.charged_credits < 0 ||
      !Number.isSafeInteger(settlement.refunded_credits) ||
      settlement.refunded_credits < 0 ||
      settlement.refunded_credits > settlement.charged_credits
    )
      return null;
    seen.add(unit.artifact);
    refunds += settlement.refunded_credits;
  }
  return Number.isSafeInteger(value.refunded_credits) && value.refunded_credits === refunds
    ? value
    : null;
}

/** @param {any} doc */
export function usesArtifactQualityProtocol(doc) {
  return (
    !!exportDelivery(doc) ||
    Object.entries(doc?.artifacts || {}).some(
      ([key, artifact]) =>
        ["report_pdf", "deck", "pptx", "one_pager"].includes(key) &&
        !!artifactQuality(artifact?.quality, key)
    )
  );
}

/** @param {any} doc @param {string} artifact @param {string | null} [jobId] */
export function exportUnit(doc, artifact, jobId = null) {
  return (
    exportDelivery(doc, jobId)?.units.find(
      (/** @type {any} */ unit) => unit.artifact === artifact
    ) || null
  );
}

/** A paid add-on rerender's durable outcome is independent of later exports.
 * @param {any} job @param {string} artifact @param {string | null} expectedJobId
 */
export function exportJobUnit(job, artifact, expectedJobId) {
  if (
    !expectedJobId ||
    job?.job_id !== expectedJobId ||
    !["completed", "failed"].includes(job.status) ||
    !Number.isSafeInteger(job.base_version) ||
    job.base_version < 1
  )
    return null;
  return exportUnit(
    { version: job.base_version, export_delivery: job.result_summary?.export_delivery },
    artifact,
    expectedJobId
  );
}

/** @param {any} unit @param {{requested?: boolean}} [options] */
export function exportUnitMessage(unit, { requested = false } = {}) {
  const label = LABELS[unit.artifact];
  const reason =
    unit.reason_code === "quality_failed"
      ? "a quality check failed"
      : unit.reason_code === "quality_not_checked"
        ? "file integrity could not be verified"
        : "the files could not be completed";
  const preserved =
    !requested && unit.preserved_outputs.length
      ? " Previously prepared files for this version are still available."
      : "";
  const refunded = unit.settlement.refunded_credits;
  const settlement = refunded > 0 ? ` ${refunded.toLocaleString()} credits refunded.` : "";
  const subject = requested
    ? `Your requested ${label} export was held`
    : `${label}: latest export held`;
  return `${subject} because ${reason}.${preserved}${settlement}`;
}

/** Quality-bearing held outputs, whether or not there was a purchased unit.
 * @param {any} doc
 */
export function heldArtifactEntries(doc) {
  return Object.entries(doc?.artifacts || {}).filter(
    ([key, artifact]) =>
      ["report_pdf", "deck", "pptx", "one_pager"].includes(key) &&
      artifact &&
      artifact.reason !== "not_purchased" &&
      !artifactUsable(artifact, key) &&
      Object.prototype.hasOwnProperty.call(artifact, "quality")
  );
}
